<template>
  <div class="space-y-6">
    <div v-if="alerts.length > 0">
      <InvoiceQuotationAlerts :alerts="alerts" data-test="approval-alerts" />
      <AiAnalysisFeedbackForm
        v-if="hasAiAlerts"
        :analysable-type="analysableType"
        :analysable-id="document.id"
        analysis-type="document_approval"
      />
    </div>

    <section v-if="costs.length > 0" data-test="approval-cost-items">
      <h2 class="text-sm font-semibold text-gray-900">{{ t("items_title") }}</h2>
      <div class="mt-2">
        <ClaimCostItemsEditor :costs="costs" />
      </div>
      <p v-if="totalAmount != null" class="mt-2 text-right text-sm text-gray-900" data-test="approval-total-amount">
        {{ t("total_label") }}
        <span class="font-bold">{{ formatPrice(totalAmount) }}</span>
      </p>
    </section>

    <section v-if="quotation" data-test="validated-quotation">
      <h2 class="text-sm font-semibold text-gray-900">{{ t("quotation_title") }}</h2>
      <a :href="quotation.url" target="_blank" class="mt-2 inline-block text-sm text-blue-700 hover:underline">
        {{ quotation.fileName }}
      </a>
      <div v-if="quotation.costs?.length > 0" class="mt-2">
        <ClaimCostItemsEditor :costs="quotation.costs" />
      </div>
      <p
        v-if="quotation.repairCost != null"
        class="mt-2 text-right text-sm text-gray-900"
        data-test="validated-quotation-total"
      >
        {{ t("total_label") }}
        <span class="font-bold">{{ formatPrice(quotation.repairCost) }}</span>
      </p>
    </section>

    <section data-test="approval-handling">
      <h2 class="text-sm font-semibold text-gray-900">{{ t("handling_title") }}</h2>
      <div class="mt-2 grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2">
        <StackedField :label="t('handling_type_label')" :value="translatedHandlingType" />
        <StackedField :label="t('refunded_entity_label')" :value="translatedRefundedEntity" />
        <StackedField :label="t('handler_label')" :value="translatedClaimHandler" />
        <StackedField :label="t('store_label')" :value="claim.store?.salesChannel" />
        <StackedField
          :label="t('max_compensation_label')"
          :value="claim.compensationValue != null ? formatPrice(claim.compensationValue) : null"
        />
      </div>
    </section>

    <section v-if="declaration?.description" data-test="declaration-description">
      <h2 class="text-sm font-semibold text-gray-900">{{ t("declaration_title") }}</h2>
      <p class="mt-2 whitespace-pre-line text-sm text-gray-700">{{ declaration.description }}</p>
    </section>
  </div>
</template>

<script>
import { useI18n } from "vue-i18n";
import { StackedField, formatPrice } from "@estaly/ui";
import AiAnalysisFeedbackForm from "@/components/claim/details/AiAnalysisFeedbackForm.vue";
import ClaimCostItemsEditor from "@/components/claim/details/ClaimCostItemsEditor.vue";
import InvoiceQuotationAlerts from "@/components/claim/details/invoice/InvoiceQuotationAlerts.vue";

export default {
  name: "ApprovalContextPanel",

  components: { AiAnalysisFeedbackForm, ClaimCostItemsEditor, InvoiceQuotationAlerts, StackedField },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  props: {
    claim: {
      type: Object,
      required: true,
    },
    // Devis ou facture en cours d'approbation : on n'affiche que ses lignes de coût.
    document: {
      type: Object,
      default: null,
    },
    // Type du document pour le retour sur l'analyse IA : "Claims::ClaimInvoice" ou "Claims::Quotation".
    analysableType: {
      type: String,
      default: "Claims::ClaimInvoice",
    },
    declaration: {
      type: Object,
      default: null,
    },
    // Devis validé à comparer à la facture, pour une réparation.
    quotation: {
      type: Object,
      default: null,
    },
  },

  computed: {
    // Écarts relevés sur le document : avec le dossier (analyse IA) et, pour une facture, avec le devis approuvé.
    alerts() {
      return this.document?.alerts || [];
    },

    // Seules les alertes issues de l'analyse IA appellent un retour sur l'analyse.
    hasAiAlerts() {
      return this.alerts.some((alert) => alert.ai);
    },

    costs() {
      return this.document?.costs || [];
    },

    totalAmount() {
      return this.document?.repairCost;
    },

    translatedHandlingType() {
      return this.claim.handlingType ? this.$t(`claim.handling_type.${this.claim.handlingType}`) : null;
    },

    translatedRefundedEntity() {
      return this.claim.refundedEntity ? this.$t(`claim.refunded_entity.${this.claim.refundedEntity}`) : null;
    },

    translatedClaimHandler() {
      return this.claim.claimHandler ? this.$t(`claim.claim_handler.${this.claim.claimHandler}`) : null;
    },
  },

  methods: {
    formatPrice,
  },
};
</script>

<i18n src="./ApprovalContextPanel.i18n.json"></i18n>
